import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import ReactMarkdown, { defaultUrlTransform } from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Send,
  Loader2,
  User,
  Bot,
  ExternalLink,
  Globe,
  ChevronDown,
  ChevronRight,
  Brain,
  Wrench,
  Zap,
  Check,
  Search,
  XCircle,
} from "lucide-react";
import {
  api,
  type Provider,
  type ProviderInfo,
  type ChatMessageRecord,
  type ChatErrorPayload,
  type ToolCallRecord,
  type WebCitation,
  type ReasoningStep,
  type ThinkingEffort,
  type TokenUsage,
  type SectionInfo,
} from "../lib/api.js";

/**
 * Subclass of Error that carries the structured ChatErrorPayload from the
 * worker through the promise rejection path. Lets the catch handler render
 * a friendly error block (kind / userMessage / errorId) instead of a raw
 * stringified exception.
 */
class ChatStreamError extends Error {
  payload: ChatErrorPayload | null;
  constructor(payload: ChatErrorPayload | null, fallback: string) {
    super(payload?.userMessage ?? fallback);
    this.name = "ChatStreamError";
    this.payload = payload;
  }
}
import { Button } from "./ui/button.js";
import { Textarea } from "./ui/forms.js";
import { cn } from "../lib/cn.js";
import { useWsFrames } from "../lib/ws-client.js";

interface SectionScope {
  sectionId: string;
  sectionName: string;
  notebookId: string;
  notebookName: string;
  siteId: string;
}

interface ChatPanelProps {
  sessionId: string | null;
  onSessionCreated: (id: string) => void;
  availableProviders: Provider[];
  providerInfo: Record<Provider, ProviderInfo>;
  sectionScope: SectionScope | null;
  onDraftSectionChange: (section: SectionInfo | null) => void;
}

const EFFORT_OPTIONS: { value: ThinkingEffort; label: string }[] = [
  { value: "low", label: "Low (fast)" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High (deep)" },
];

type StreamEvent =
  | { type: "reasoning"; id: string; text: string }
  | { type: "tool-call"; toolCallId: string; name: string; args?: unknown; status: "running" | "done" | "error"; result?: unknown; error?: unknown }
  | { type: "text"; text: string }
  | { type: "source"; title: string; url?: string };

export function ChatPanel({
  sessionId,
  onSessionCreated,
  availableProviders,
  providerInfo,
  sectionScope,
  onDraftSectionChange,
}: ChatPanelProps) {
  const qc = useQueryClient();
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  // Optimistic user messages keyed by sessionId (or "__new__" for a
  // not-yet-created session). Per-session so switching chat tabs during
  // an in-flight turn doesn't wipe the bubble.
  const [pendingBySession, setPendingBySession] = useState<
    Record<string, ChatMessageRecord[]>
  >({});
  const [draftEffort, setDraftEffort] = useState<ThinkingEffort>("medium");
  const [draftProvider, setDraftProvider] = useState<Provider>("openai");
  const [draftModel, setDraftModel] = useState<string>("");
  const [streamingEvents, setStreamingEvents] = useState<StreamEvent[]>([]);
  const [streamingSessionId, setStreamingSessionId] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Live turn state. The POST handler returns a jobId; incoming WebSocket
  // frames with matching jobId drive streamingEvents. `done` resolves the
  // turn and clears this ref.
  const turnRef = useRef<{
    jobId: string;
    resolve: (sessionId: string) => void;
    reject: (err: Error) => void;
  } | null>(null);
  // Tracks WS liveness for the stall watchdog. Reset on submit; bumped on
  // every chunk for the active jobId. If WS goes silent for too long, the
  // watchdog refetches the session and closes the turn against any
  // newly-persisted assistant message (chat-worker writes partials every 2s).
  const turnStartedAtRef = useRef<number>(0);
  const lastChunkAtRef = useRef<number>(0);

  useWsFrames((frame) => {
    if (frame.kind !== "chat") return;
    const current = turnRef.current;
    const jobId = frame.jobId as string | undefined;
    if (!current || jobId !== current.jobId) return;
    lastChunkAtRef.current = Date.now();
    const chunk = frame.chunk as {
      type: string;
      [k: string]: unknown;
    };
    switch (chunk.type) {
        case "text-delta": {
          const text = chunk.text as string;
          setStreamingEvents((evts) => {
            const last = evts[evts.length - 1];
            if (last?.type === "text") {
              return [...evts.slice(0, -1), { ...last, text: last.text + text }];
            }
            return [...evts, { type: "text", text }];
          });
          break;
        }
        case "reasoning-delta": {
          const id = chunk.id as string;
          const text = chunk.text as string;
          setStreamingEvents((evts) => {
            const last = evts[evts.length - 1];
            if (last?.type === "reasoning" && last.id === id) {
              return [...evts.slice(0, -1), { ...last, text: last.text + text }];
            }
            return [...evts, { type: "reasoning", id, text }];
          });
          break;
        }
        case "tool-call":
          setStreamingEvents((evts) => [
            ...evts,
            {
              type: "tool-call",
              toolCallId: chunk.toolCallId as string,
              name: chunk.toolName as string,
              args: chunk.args,
              status: "running",
            },
          ]);
          break;
        case "tool-result":
          setStreamingEvents((evts) =>
            evts.map((e) =>
              e.type === "tool-call" && e.toolCallId === chunk.toolCallId
                ? { ...e, status: "done" as const, result: chunk.result }
                : e,
            ),
          );
          break;
        case "tool-error":
          setStreamingEvents((evts) =>
            evts.map((e) =>
              e.type === "tool-call" && e.toolCallId === chunk.toolCallId
                ? { ...e, status: "error" as const, error: chunk.error }
                : e,
            ),
          );
          break;
        case "source":
          setStreamingEvents((evts) => [
            ...evts,
            { type: "source", title: chunk.title as string, url: chunk.url as string },
          ]);
          break;
        case "done":
          turnRef.current = null;
          current.resolve(chunk.sessionId as string);
          break;
        case "error": {
          turnRef.current = null;
          // chunk.error is either a string (legacy) or a ChatErrorPayload
          // object (worker post-classification). Normalize before throwing.
          const e = chunk.error;
          let payload: ChatErrorPayload | null = null;
          let fallback = "Chat worker error";
          if (typeof e === "string") {
            fallback = e;
          } else if (e && typeof e === "object" && "userMessage" in e) {
            payload = e as ChatErrorPayload;
          }
          current.reject(new ChatStreamError(payload, fallback));
          break;
        }
    }
  });

  useEffect(() => {
    if (availableProviders.length === 0) return;
    if (!availableProviders.includes(draftProvider)) {
      const p = availableProviders[0];
      setDraftProvider(p);
      setDraftModel(providerInfo[p]?.defaultModel ?? "");
    } else if (!draftModel) {
      setDraftModel(providerInfo[draftProvider]?.defaultModel ?? "");
    }
  }, [availableProviders, providerInfo, draftProvider, draftModel]);

  useEffect(() => {
    setInput("");
  }, [sessionId]);

  // Stall watchdog. If the WebSocket goes silent for >STALL_MS while a turn
  // is in flight, refetch the session — chat-worker upserts partial
  // assistant text every 2s, so a freshly-persisted assistant message means
  // we should close the turn and let `session.messages` render the answer
  // instead of waiting forever on a dead WS.
  useEffect(() => {
    if (!busy || !sessionId) return;
    const STALL_MS = 30_000;
    const POLL_MS = 10_000;
    const interval = setInterval(async () => {
      const t = turnRef.current;
      if (!t) return;
      if (Date.now() - lastChunkAtRef.current < STALL_MS) return;
      try {
        const fresh = await api.getSession(sessionId);
        qc.setQueryData(["session", sessionId], fresh);
        const startedAt = turnStartedAtRef.current;
        const newAssistant = fresh.messages.find(
          (m) =>
            m.role === "assistant" &&
            new Date(m.createdAt).getTime() >= startedAt,
        );
        if (newAssistant && turnRef.current === t) {
          turnRef.current = null;
          t.resolve(sessionId);
        }
      } catch {
        /* keep polling */
      }
    }, POLL_MS);
    return () => clearInterval(interval);
  }, [busy, sessionId, qc]);

  const currentSessionKey = sessionId ?? "__new__";
  const pending = pendingBySession[currentSessionKey] ?? [];
  const setPendingForKey = (
    key: string,
    updater: (p: ChatMessageRecord[]) => ChatMessageRecord[],
  ) =>
    setPendingBySession((m) => ({ ...m, [key]: updater(m[key] ?? []) }));

  const sessionQ = useQuery({
    queryKey: ["session", sessionId],
    queryFn: () => api.getSession(sessionId!),
    enabled: !!sessionId,
  });

  const session = sessionQ.data;
  const isStreaming = busy && streamingSessionId === currentSessionKey && streamingEvents.length > 0;
  const isMyTurnPending = busy && streamingSessionId === currentSessionKey;
  const messages: ChatMessageRecord[] = useMemo(() => {
    const base = [...(session?.messages ?? []), ...pending];
    if (isStreaming) {
      const textEvent = streamingEvents.find((e) => e.type === "text");
      base.push({
        id: "streaming",
        role: "assistant",
        content: textEvent?.type === "text" ? textEvent.text || "..." : "...",
        createdAt: new Date().toISOString(),
      });
    }
    return base;
  }, [session?.messages, pending, isStreaming, streamingEvents]);

  const citedPageIds = useMemo(() => {
    const ids = new Set<string>();
    const re = /\[onenote:([^\]\s]+)\]|\[([^\]]*)\]\(onenote:([^)]+)\)|(?<![[(\w])onenote:([^\s]+)/g;
    for (const msg of messages) {
      let m: RegExpExecArray | null;
      while ((m = re.exec(msg.content)) !== null) {
        const raw = m[1] ?? m[3] ?? m[4];
        if (!raw) continue;
        ids.add(extractPageIdFromOneNoteRef(raw));
      }
    }
    return [...ids];
  }, [messages]);

  const pageMetaQ = useQuery({
    queryKey: ["pageMeta", citedPageIds],
    queryFn: async () => {
      if (citedPageIds.length === 0) return {};
      const { pages } = await api.corpusStatus({ pageIds: citedPageIds });
      const m: Record<string, { title: string; webUrl: string | null }> = {};
      for (const p of pages) m[p.pageId] = { title: p.title, webUrl: p.webUrl };
      return m;
    },
    enabled: citedPageIds.length > 0,
  });

  const pageMeta = pageMetaQ.data ?? {};

  const sectionsQ = useQuery({
    queryKey: ["corpusSections"],
    queryFn: api.listCorpusSections,
    // Both ChatPanel and CorpusBrowser are persistently mounted (toggled
    // with display:none in App.tsx), so switching tabs does NOT remount.
    // Tighten staleTime + window-focus refetch so the dropdown still
    // refreshes when the user comes back from elsewhere; the WS listener
    // below handles the in-app sync-progress signal.
    staleTime: 15_000,
    refetchOnWindowFocus: true,
  });

  // Refresh the section dropdown as new pages land in corpus.pages. Without
  // this, the cache stays bound to whatever was visible at first mount —
  // a fresh sync writes pages but the dropdown looks frozen.
  useWsFrames((frame) => {
    if (frame.kind !== "sync_progress") return;
    const p = frame.progress as
      | { phase?: string; pagesDone?: number; status?: string }
      | undefined;
    if (!p) return;
    const pagesDone = p.pagesDone ?? 0;
    // Debounce: invalidate every 25 pages, on phase flip into syncing,
    // and on completion. Avoids hammering the API on every progress tick.
    const advancing =
      p.phase === "syncing" && pagesDone > 0 && pagesDone % 25 === 0;
    const finished = p.status === "completed";
    if (advancing || finished) {
      void qc.invalidateQueries({ queryKey: ["corpusSections"] });
    }
  });

  const sectionDisabled = busy || (session?.messages?.length ?? 0) > 0;

  const setSectionMut = useMutation({
    mutationFn: (s: SectionInfo) =>
      api.patchSession(sessionId!, {
        sectionId: s.sectionId,
        sectionName: s.sectionName,
        notebookId: s.notebookId,
        notebookName: s.notebookName,
        siteId: s.siteId,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["session", sessionId] });
    },
  });

  const handleSectionChange = (key: string) => {
    if (!key) {
      onDraftSectionChange(null);
      return;
    }
    const sec = (sectionsQ.data?.sections ?? []).find(
      (s) => `${s.notebookId}:${s.sectionId}` === key,
    );
    if (!sec) return;
    if (session) {
      setSectionMut.mutate(sec);
    } else {
      onDraftSectionChange(sec);
    }
  };

  const sectionLabel = (s: SectionInfo) => {
    const prefix = s.notebookName + " / ";
    const name = s.sectionName.startsWith(prefix)
      ? s.sectionName.slice(prefix.length)
      : s.sectionName;
    return `${s.notebookName} / ${name}`;
  };

  const effort: ThinkingEffort = session?.thinkingEffort ?? draftEffort;
  const provider: Provider = session?.provider ?? draftProvider;
  const model: string = session?.model ?? draftModel;
  const currentProviderInfo = providerInfo[provider];

  const setEffortMut = useMutation({
    mutationFn: (e: ThinkingEffort) =>
      api.patchSession(sessionId!, { thinkingEffort: e }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["session", sessionId] }),
  });

  const setProviderModelMut = useMutation({
    mutationFn: (opts: { provider: Provider; model: string }) =>
      api.patchSession(sessionId!, opts),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["session", sessionId] }),
  });

  const handleProviderChange = (p: Provider) => {
    const defaultModel = providerInfo[p]?.defaultModel ?? "";
    if (sessionId) {
      setProviderModelMut.mutate({ provider: p, model: defaultModel });
    } else {
      setDraftProvider(p);
      setDraftModel(defaultModel);
    }
  };

  const handleModelChange = (m: string) => {
    if (sessionId) {
      setProviderModelMut.mutate({ provider, model: m });
    } else {
      setDraftModel(m);
    }
  };

  const configDisabled = busy || setEffortMut.isPending || setProviderModelMut.isPending;

  useEffect(() => {
    if (isStreaming && scrollRef.current) {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    }
  }, [isStreaming, streamingEvents]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;

    if (!sectionScope) return;

    const tempUser: ChatMessageRecord = {
      id: `tmp-u-${Date.now()}`,
      role: "user",
      content: text,
      createdAt: new Date().toISOString(),
    };
    const targetSessionId = sessionId ?? "__new__";
    setPendingForKey(targetSessionId, (p) => [...p, tempUser]);
    setInput("");
    setBusy(true);
    setStreamingEvents([]);
    setStreamingSessionId(targetSessionId);
    turnStartedAtRef.current = Date.now();
    lastChunkAtRef.current = Date.now();
    abortRef.current = new AbortController();

    try {
      const submitResult = await api.chatSubmit({
        message: text,
        sessionId: sessionId ?? undefined,
        provider: sessionId ? undefined : draftProvider,
        model: sessionId ? undefined : draftModel,
        thinkingEffort: sessionId ? undefined : draftEffort,
        sectionId: sessionId ? undefined : sectionScope.sectionId,
        sectionName: sessionId ? undefined : sectionScope.sectionName,
        notebookId: sessionId ? undefined : sectionScope.notebookId,
        notebookName: sessionId ? undefined : sectionScope.notebookName,
        siteId: sessionId ? undefined : sectionScope.siteId,
      });

      const newSessionId = await new Promise<string>((resolve, reject) => {
        turnRef.current = { jobId: submitResult.jobId, resolve, reject };
      });

      if (!sessionId) onSessionCreated(newSessionId);
      void qc.invalidateQueries({ queryKey: ["sessions"] });
      void qc.invalidateQueries({ queryKey: ["session", newSessionId] });
      setPendingForKey(targetSessionId, () => []);
      // If the turn started with no sessionId, the pending bubble lives
      // under "__new__". The server-saved message will now show under the
      // real sessionId, so clear the new key too just in case.
      if (targetSessionId === "__new__")
        setPendingForKey(newSessionId, () => []);
      setStreamingEvents([]);
      setStreamingSessionId(null);
    } catch (err) {
      if (err instanceof DOMException && err.name === "AbortError") {
        setPendingForKey(targetSessionId, () => []);
        setStreamingEvents([]);
        setStreamingSessionId(null);
      } else {
        // Synthetic assistant message rendered by ChatTurn — when payload
        // is present, the bubble switches to the friendly-error block.
        const payload =
          err instanceof ChatStreamError ? err.payload : null;
        const fallbackMsg = err instanceof Error ? err.message : String(err);
        const errMsg: ChatMessageRecord & { __errorPayload?: ChatErrorPayload } = {
          id: `tmp-err-${Date.now()}`,
          role: "assistant",
          content: payload?.userMessage ?? fallbackMsg,
          createdAt: new Date().toISOString(),
          __errorPayload: payload ?? undefined,
        };
        setPendingForKey(targetSessionId, (p) => [...p, errMsg]);
        setStreamingEvents([]);
        setStreamingSessionId(null);
      }
    } finally {
      setBusy(false);
      abortRef.current = null;
    }
  };

  const stop = () => {
    abortRef.current?.abort();
  };

  return (
    <div className="flex h-full flex-col gap-2">
      <div className="flex items-center gap-3 text-[11px] text-slate-500">
        <Brain className="h-3 w-3" />
        <label className="flex items-center gap-1">
          Thinking:
          <select
            value={effort}
            disabled={configDisabled}
            onChange={(e) => {
              const next = e.target.value as ThinkingEffort;
              if (sessionId) setEffortMut.mutate(next);
              else setDraftEffort(next);
            }}
            className="rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[11px]"
          >
            {EFFORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-1">
          Provider:
          <select
            value={provider}
            disabled={configDisabled || availableProviders.length <= 1}
            onChange={(e) => handleProviderChange(e.target.value as Provider)}
            className="rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[11px]"
          >
            {availableProviders.map((p) => (
              <option key={p} value={p}>
                {providerInfo[p]?.label ?? p}
              </option>
            ))}
          </select>
        </label>
        {currentProviderInfo && currentProviderInfo.models.length > 1 && (
          <label className="flex items-center gap-1">
            Model:
            <select
              value={model}
              disabled={configDisabled}
              onChange={(e) => handleModelChange(e.target.value)}
              className="rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[11px]"
            >
              {currentProviderInfo.models.map((m) => (
                <option key={m} value={m}>
                  {m}
                  {m === currentProviderInfo.defaultModel ? " (default)" : ""}
                </option>
              ))}
            </select>
          </label>
        )}
        <label className="flex items-center gap-1">
          Section:
          <select
            value={sectionScope ? `${sectionScope.notebookId}:${sectionScope.sectionId}` : ""}
            disabled={sectionDisabled}
            onChange={(e) => handleSectionChange(e.target.value)}
            className="max-w-[200px] rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[11px]"
          >
            {!sectionScope && (
              <option value="">Select a section...</option>
            )}
            {(() => {
              const sections = sectionsQ.data?.sections ?? [];
              const byNotebook = new Map<string, SectionInfo[]>();
              for (const s of sections) {
                const key = s.notebookId;
                if (!byNotebook.has(key)) byNotebook.set(key, []);
                byNotebook.get(key)!.push(s);
              }
              return [...byNotebook.entries()].map(([nbId, secs]) => (
                <optgroup key={nbId} label={secs[0].notebookName}>
                  {secs.map((s) => (
                    <option key={`${s.notebookId}:${s.sectionId}`} value={`${s.notebookId}:${s.sectionId}`}>
                      {sectionLabel(s)}
                    </option>
                  ))}
                </optgroup>
              ));
            })()}
          </select>
        </label>
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto pr-2" ref={scrollRef}>
        {messages.length === 0 && !isStreaming && (
          <div className="flex h-full items-center justify-center text-center text-sm text-slate-400">
            Ask anything. Search the corpus, read pages, or ask a question that
            blends OneNote with web research.
          </div>
        )}
        {messages.map((msg) =>
          msg.id === "streaming" ? (
            <StreamingBubble
              key="streaming"
              events={streamingEvents}
              pageMeta={pageMeta}
            />
          ) : (
            <MessageBubble key={msg.id} message={msg} pageMeta={pageMeta} />
          ),
        )}
        {isMyTurnPending && !isStreaming && (
          <div className="flex items-center gap-2 px-3 text-sm text-slate-500">
            <Loader2 className="h-3 w-3 animate-spin" />
            Thinking...
          </div>
        )}
      </div>

      <div className="flex gap-2 border-t border-slate-200 pt-3">
        <Textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={sectionScope ? "Ask the agent..." : "Select a section first..."}
          rows={2}
          disabled={!sectionScope}
          onKeyDown={(e) => {
            if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
              e.preventDefault();
              send();
            }
          }}
        />
        {isMyTurnPending ? (
          <Button onClick={stop} variant="destructive">
            <Loader2 className="h-4 w-4" /> Stop
          </Button>
        ) : (
          <Button onClick={send} disabled={!input.trim() || !sectionScope || busy}>
            <Send className="h-4 w-4" />
          </Button>
        )}
      </div>
      <p className="text-xs text-slate-400">Cmd/Ctrl + Enter to send</p>
    </div>
  );
}

function StreamingBubble({
  events,
  pageMeta,
}: {
  events: StreamEvent[];
  pageMeta: Record<string, { title: string; webUrl: string | null }>;
}) {
  const [openExplore, setOpenExplore] = useState<Record<string, boolean>>({});
  return (
    <div className="flex gap-2">
      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-slate-200 text-slate-700">
        <Bot className="h-3 w-3" />
      </div>
      <div className="max-w-[85%] rounded-lg bg-slate-100 px-3 py-2 text-sm text-slate-800">
        {events.map((evt, i) => {
          switch (evt.type) {
            case "reasoning":
              return (
                <div key={i} className="mb-1 border-l-2 border-slate-200 pl-3 italic text-slate-400 text-xs">
                  <span className="font-medium">Thinking: </span>{evt.text}
                </div>
              );
            case "tool-call":
              return (
                <div key={i} className="mb-1 italic text-slate-400 text-xs">
                  {evt.status === "running" ? (
                    <span className="inline-flex items-center gap-1">
                      <Loader2 className="h-2.5 w-2.5 animate-spin" />
                      {evt.name === "agent-explore"
                        ? "Delegating to explore agent..."
                        : `Using ${evt.name}...`}
                    </span>
                  ) : evt.status === "error" ? (
                    <span className="inline-flex items-center gap-1 text-red-500">
                      <XCircle className="h-2.5 w-2.5" />
                      {evt.name} failed
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1">
                      <Check className="h-2.5 w-2.5 text-green-500" />
                      {evt.name === "agent-explore"
                        ? "Explore agent completed"
                        : `${evt.name} completed`}
                      {evt.name === "agent-explore" && evt.result != null && (
                        <button
                          onClick={() =>
                            setOpenExplore((o) => ({
                              ...o,
                              [evt.toolCallId]: !o[evt.toolCallId],
                            }))
                          }
                          className="ml-1 text-blue-500 hover:text-blue-700"
                        >
                          {openExplore[evt.toolCallId] ? "Hide" : "Show result"}
                        </button>
                      )}
                    </span>
                  )}
                  {evt.name === "agent-explore" && openExplore[evt.toolCallId] && evt.result != null && (
                    <pre className="mt-1 max-h-48 overflow-auto whitespace-pre-wrap rounded bg-white/70 p-2 text-[10px] text-slate-600 not-italic">
                      {String(typeof evt.result === "string"
                        ? evt.result.slice(0, 2000)
                        : JSON.stringify(evt.result, null, 2).slice(0, 2000))}
                    </pre>
                  )}
                </div>
              );
            case "text":
              return <ChatMarkdown key={i} content={evt.text} pageMeta={pageMeta} />;
            case "source":
              return (
                <span
                  key={i}
                  className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-700"
                >
                  <Search className="h-2.5 w-2.5" />
                  {evt.url ? (
                    <a href={evt.url} target="_blank" rel="noreferrer" className="hover:underline">
                      {evt.title || safeHost(evt.url)}
                    </a>
                  ) : (
                    evt.title
                  )}
                </span>
              );
          }
        })}
        <div className="mt-1 flex items-center gap-1 italic text-slate-400 text-xs">
          <Loader2 className="h-2.5 w-2.5 animate-spin" />
          Thinking...
        </div>
      </div>
    </div>
  );
}

function MessageBubble({
  message,
  pageMeta,
}: {
  message: ChatMessageRecord;
  pageMeta: Record<string, { title: string; webUrl: string | null }>;
}) {
  const isUser = message.role === "user";
  // Synthetic error messages from the catch handler carry __errorPayload
  // (transient, not persisted server-side). Render them as a friendly
  // error card instead of a regular bubble so users see plain English +
  // an actionable next step + the trace ID for support.
  const errorPayload = (message as ChatMessageRecord & {
    __errorPayload?: ChatErrorPayload;
  }).__errorPayload;
  if (!isUser && errorPayload) {
    return <ChatErrorBubble payload={errorPayload} />;
  }
  const hasCacheInfo = !isUser && message.usage && message.usage.inputTokens > 0;
  const hasDetails =
    !isUser &&
    ((message.reasoning && message.reasoning.length > 0) ||
      (message.toolCalls && message.toolCalls.length > 0) ||
      (message.webCitations && message.webCitations.length > 0) ||
      hasCacheInfo);

  const [open, setOpen] = useState(false);

  return (
    <div className={cn("flex gap-2", isUser && "flex-row-reverse")}>
      <div
        className={cn(
          "flex h-7 w-7 shrink-0 items-center justify-center rounded-full",
          isUser ? "bg-slate-900 text-white" : "bg-slate-200 text-slate-700",
        )}
      >
        {isUser ? <User className="h-3 w-3" /> : <Bot className="h-3 w-3" />}
      </div>
      <div
        className={cn(
          "max-w-[85%] rounded-lg px-3 py-2 text-sm",
          isUser ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-800",
        )}
      >
        {isUser ? (
          <div className="whitespace-pre-wrap">{message.content}</div>
        ) : (
          <ChatMarkdown content={message.content} pageMeta={pageMeta} />
        )}

        {message.webCitations && message.webCitations.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {message.webCitations.map((c, i) => (
              <a
                key={i}
                href={c.url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 rounded-full bg-blue-100 px-2 py-0.5 text-xs text-blue-700 hover:bg-blue-200"
                title={c.url}
              >
                <Globe className="h-2.5 w-2.5" />
                {c.title ?? safeHost(c.url)}
              </a>
            ))}
          </div>
        )}

        {hasDetails && (
          <div className="mt-2 border-t border-slate-200/60 pt-2 text-xs">
            <button
              onClick={() => setOpen((v) => !v)}
              className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-700"
            >
              {open ? (
                <ChevronDown className="h-3 w-3" />
              ) : (
                <ChevronRight className="h-3 w-3" />
              )}
              Show details
              {message.reasoning && message.reasoning.length > 0 && (
                <span className="ml-1 inline-flex items-center gap-0.5 text-slate-400">
                  · <Brain className="h-2.5 w-2.5" />
                  {message.reasoning.length}
                </span>
              )}
              {message.toolCalls && message.toolCalls.length > 0 && (
                <span className="ml-1 inline-flex items-center gap-0.5 text-slate-400">
                  · <Wrench className="h-2.5 w-2.5" />
                  {message.toolCalls.length}
                </span>
              )}
              {hasCacheInfo && message.usage!.cachedTokens > 0 && (
                <span
                  className="ml-1 inline-flex items-center gap-0.5 text-emerald-600"
                  title={`${message.usage!.cachedTokens.toLocaleString()} of ${message.usage!.inputTokens.toLocaleString()} input tokens served from cache`}
                >
                  · <Zap className="h-2.5 w-2.5" />
                  {Math.round(
                    (message.usage!.cachedTokens / message.usage!.inputTokens) * 100,
                  )}%
                </span>
              )}
            </button>

            {open && (
              <div className="mt-2 space-y-3">
                {message.usage && message.usage.inputTokens > 0 && (
                  <UsageSection usage={message.usage} />
                )}
                {message.reasoning && message.reasoning.length > 0 && (
                  <ReasoningSection reasoning={message.reasoning} />
                )}
                {message.toolCalls && message.toolCalls.length > 0 && (
                  <ToolCallsSection toolCalls={message.toolCalls} />
                )}
                {message.webCitations && message.webCitations.length > 0 && (
                  <WebSourcesSection citations={message.webCitations} />
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function UsageSection({ usage }: { usage: TokenUsage }) {
  const pct =
    usage.inputTokens > 0
      ? Math.round((usage.cachedTokens / usage.inputTokens) * 100)
      : 0;
  const fmt = (n: number) => n.toLocaleString();
  return (
    <div>
      <div className="mb-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
        <Zap className="h-3 w-3" /> Usage
      </div>
      <div className="text-[11px] text-slate-700">
        {usage.cachedTokens > 0 ? (
          <>
            <span className="font-medium">
              {fmt(usage.cachedTokens)} / {fmt(usage.inputTokens)} input tokens cached ({pct}%)
            </span>{" "}
            · {fmt(usage.outputTokens)} output
            {usage.reasoningTokens > 0 && <> · {fmt(usage.reasoningTokens)} reasoning</>}
          </>
        ) : (
          <>
            {fmt(usage.inputTokens)} input · {fmt(usage.outputTokens)} output
            {usage.reasoningTokens > 0 && <> · {fmt(usage.reasoningTokens)} reasoning</>}
          </>
        )}
      </div>
    </div>
  );
}

function ReasoningSection({ reasoning }: { reasoning: ReasoningStep[] }) {
  return (
    <div>
      <div className="mb-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
        <Brain className="h-3 w-3" /> Reasoning
      </div>
      <ol className="space-y-2 text-[11px]">
        {reasoning.map((step) => (
          <li key={step.step}>
            <div className="font-medium text-slate-600">Step {step.step}</div>
            <ul className="mt-0.5 list-disc space-y-0.5 pl-4 text-slate-700">
              {step.summaries.map((s, i) => (
                <li key={i}>{s}</li>
              ))}
            </ul>
          </li>
        ))}
      </ol>
    </div>
  );
}

function ToolCallsSection({ toolCalls }: { toolCalls: ToolCallRecord[] }) {
  return (
    <div>
      <div className="mb-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
        <Wrench className="h-3 w-3" /> Tool calls
      </div>
      <ul className="space-y-1.5">
        {toolCalls.map((c, i) => (
          <li key={i} className="rounded bg-white/70 px-2 py-1.5">
            <div className="flex items-center gap-1 text-[11px] font-mono">
              <span className="text-slate-700">{c.name}</span>
              {c.error && <span className="text-red-600">— {c.error}</span>}
            </div>
            <details className="mt-0.5">
              <summary className="cursor-pointer text-[10px] text-slate-400">
                args
              </summary>
              <pre className="mt-0.5 max-h-32 overflow-auto rounded bg-slate-50 p-1 text-[10px]">
                {JSON.stringify(c.arguments, null, 2)}
              </pre>
            </details>
            {c.output !== undefined && (
              <details className="mt-0.5">
                <summary className="cursor-pointer text-[10px] text-slate-400">
                  result
                </summary>
                <pre className="mt-0.5 max-h-40 overflow-auto rounded bg-slate-50 p-1 text-[10px]">
                  {JSON.stringify(c.output, null, 2)}
                </pre>
              </details>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

function WebSourcesSection({ citations }: { citations: WebCitation[] }) {
  return (
    <div>
      <div className="mb-1 flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
        <Globe className="h-3 w-3" /> Web sources
      </div>
      <ul className="space-y-0.5 text-[11px]">
        {citations.map((c, i) => (
          <li key={i} className="truncate">
            <a
              href={c.url}
              target="_blank"
              rel="noreferrer"
              className="text-blue-700 hover:underline"
            >
              {c.title ?? c.url}
            </a>
            <span className="ml-1 text-slate-400">{safeHost(c.url)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function safeHost(u: string): string {
  try {
    return new URL(u).hostname;
  } catch {
    return u;
  }
}

function extractText(node: unknown): string {
  if (typeof node === "string") return node;
  if (typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(extractText).join("");
  if (node && typeof node === "object" && "props" in (node as any)) {
    return extractText((node as any).props.children);
  }
  return "";
}

const CITATION_RE = /\[onenote:([^\]\s]+)\](?!\()/g;
const BARE_RE = /(?<![[(\w])onenote:([^\s]+)/g;

/**
 * Accepts either:
 *   - a bare OneNote page GUID (canonical citationHint format), or
 *   - a OneNote desktop deep-link fragment like
 *     "#23\\07\\31-...&section-id={...}&page-id={GUID}&end"
 * Returns a clean lowercase GUID if one can be extracted, else the input.
 */
function extractPageIdFromOneNoteRef(raw: string): string {
  const guidRe = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
  const pageIdParam = /page-id=\{([0-9a-f-]+)\}/i.exec(raw);
  if (pageIdParam) return pageIdParam[1].toLowerCase();
  const guid = guidRe.exec(raw);
  if (guid) return guid[0].toLowerCase();
  return raw;
}

function preprocessCitations(text: string): string {
  let out = text.replace(CITATION_RE, (_match, id: string) => `[onenote:${id}](onenote:${id})`);
  out = out.replace(BARE_RE, (_match, id: string) => `[onenote:${id}](onenote:${id})`);
  return out;
}

function ChatMarkdown({
  content,
  pageMeta,
}: {
  content: string;
  pageMeta: Record<string, { title: string; webUrl: string | null }>;
}) {
  const md = preprocessCitations(content);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const link = ({ href, children }: any) => {
    if (!href) return <>{children}</>;
    if (href.startsWith("onenote:")) {
      const rawRef = href.slice("onenote:".length);
      const id = extractPageIdFromOneNoteRef(rawRef);
      const meta = pageMeta[id];
      const childText = extractText(children);
      const title = meta?.title || childText || id;
      // Fallback chain: SharePoint webUrl (opens in browser) → the raw
      // onenote: protocol URL (opens OneNote desktop app if installed).
      // Always render as <a> so the chip stays clickable even when
      // pageMeta hasn't resolved yet.
      const linkHref = meta?.webUrl ?? href;
      const chip = (
        <>
          <span className="truncate max-w-[160px]">{title.slice(0, 32)}</span>
          <ExternalLink className="h-2.5 w-2.5 shrink-0" />
        </>
      );
      return (
        <a
          href={linkHref}
          target="_blank"
          rel="noreferrer"
          className="not-prose mx-0.5 inline-flex items-center gap-0.5 rounded bg-emerald-100 px-1.5 py-0.5 text-xs text-emerald-800 no-underline hover:bg-emerald-200"
          title={`Open "${title}" in OneNote`}
        >
          {chip}
        </a>
      );
    }
    return (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        className="text-blue-600 underline hover:text-blue-800"
      >
        {children}
      </a>
    );
  };

  return (
    <div className="prose prose-sm prose-slate max-w-none prose-p:my-1 prose-li:my-0.5 prose-ul:my-1 prose-ol:my-1 prose-headings:mb-1 prose-headings:mt-2 prose-table:my-2 prose-pre:my-2 prose-pre:rounded prose-pre:bg-slate-50 prose-pre:p-2 prose-pre:text-[11px] prose-code:rounded prose-code:bg-slate-100 prose-code:px-1 prose-code:text-slate-800 prose-th:border prose-th:border-slate-200 prose-th:bg-slate-50 prose-th:px-2 prose-th:py-1 prose-td:border prose-td:border-slate-200 prose-td:px-2 prose-td:py-1">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{ a: link }}
        urlTransform={(uri) => (uri.startsWith("onenote:") ? uri : defaultUrlTransform(uri))}
      >
        {md}
      </ReactMarkdown>
    </div>
  );
}

/**
 * Friendly error bubble shown in place of a normal assistant message when
 * the worker emits a structured error chunk. Same rate-limited voice as
 * the sync banners: state what happened, name who's at fault, and tell
 * the user what to do next. Trace id is copy-clickable for support.
 */
function ChatErrorBubble({ payload }: { payload: ChatErrorPayload }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex gap-2">
      <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-rose-100 text-rose-700">
        <XCircle className="h-3 w-3" />
      </div>
      <div className="max-w-[85%] rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-900">
        <div>{payload.userMessage}</div>
        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-rose-700/80">
          <code className="rounded bg-rose-100 px-1 py-0.5">{payload.kind}</code>
          {payload.errorId && (
            <button
              onClick={() => {
                void navigator.clipboard.writeText(payload.errorId!);
                setCopied(true);
                setTimeout(() => setCopied(false), 1200);
              }}
              className="inline-flex items-center gap-1 hover:text-rose-900"
              title="Copy trace ID for support"
            >
              <code className="font-mono">{payload.errorId}</code>
              {copied && <span className="text-emerald-700">copied</span>}
            </button>
          )}
          {payload.providerTraceId && (
            <span title="Upstream provider trace ID">
              provider: <code className="font-mono">{payload.providerTraceId}</code>
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
